import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { $api } from '../../api';
import { useOrganization } from '../../contexts/OrganizationContext';
import type { paths } from '../../api/paths';
import ErrorMessage from '../ErrorMessage';

type ActivityLog = paths['/api/organizations/{organization_id}/activity-logs']['get']['responses']['200']['content']['application/json']['activities'][0];

const RecentActivities: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id || '';
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allActivities, setAllActivities] = useState<ActivityLog[]>([]);

  // Reset activities when organization changes
  useEffect(() => {
    setAllActivities([]);
    setCursor(undefined);
  }, [orgId]);

  // Fetch activity logs with cursor pagination
  const activitiesQuery = $api.useQuery(
    'get',
    '/api/organizations/{organization_id}/activity-logs',
    {
      params: {
        path: {
          organization_id: orgId,
        },
        query: {
          cursor,
          limit: '10',
        },
      },
    },
    {
      enabled: !!orgId,
    }
  );

  // Accumulate activities as we paginate
  useEffect(() => {
    if (activitiesQuery.data?.activities) {
      if (cursor === undefined) {
        // First page - replace all activities
        setAllActivities(activitiesQuery.data.activities);
      } else {
        // Subsequent pages - append to existing activities
        setAllActivities(prev => [...prev, ...activitiesQuery.data!.activities]);
      }
    }
  }, [activitiesQuery.data?.activities, cursor]);

  const hasMore = activitiesQuery.data?.has_more ?? false;
  const nextCursor = activitiesQuery.data?.next_cursor;
  const isLoading = activitiesQuery.isLoading;
  const isLoadingMore = isLoading && cursor !== undefined;

  const handleLoadMore = () => {
    if (nextCursor && !isLoadingMore) {
      setCursor(nextCursor);
    }
  };

  const getActivityLabel = (activity: ActivityLog): string => {
    const resourceName = activity.resource_name || 'Ressource';
    
    switch (activity.activity_type) {
      case 'entity_created':
        return `Entité créée: ${resourceName}`;
      case 'entity_updated':
        return `Entité modifiée: ${resourceName}`;
      case 'entity_deleted':
        return `Entité supprimée: ${resourceName}`;
      case 'user_invited':
        return `Utilisateur invité: ${resourceName}`;
      case 'alert_created':
        return `Alerte créée: ${resourceName}`;
      case 'alert_resolved':
        return `Alerte résolue: ${resourceName}`;
      default:
        return `Activité: ${activity.activity_type}`;
    }
  };

  const getActivityStatus = (activity: ActivityLog): 'success' | 'warning' | 'info' => {
    switch (activity.activity_type) {
      case 'entity_deleted':
        return 'warning';
      case 'alert_resolved':
      case 'entity_created':
        return 'success';
      default:
        return 'info';
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  // Show skeleton on initial load
  if (isLoading && allActivities.length === 0) {
    return <RecentActivitiesSkeleton />;
  }

  if (activitiesQuery.error && allActivities.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <ErrorMessage error={activitiesQuery.error} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Activité Récente</h3>
      
      {allActivities.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">Aucune activité récente</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {allActivities.map((activity) => {
              const status = getActivityStatus(activity);
              return (
                <div key={activity.id} className="flex items-center space-x-4 py-3 border-b border-gray-100 last:border-b-0">
                  <div className={`w-2 h-2 rounded-full ${
                    status === 'success' ? 'bg-green-500' :
                    status === 'warning' ? 'bg-yellow-500' :
                    'bg-blue-500'
                  }`} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{getActivityLabel(activity)}</p>
                    {activity.resource_name && (
                      <p className="text-xs text-gray-500">{activity.resource_name}</p>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">{formatTimeAgo(activity.created_at)}</span>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div className="mt-4 flex justify-center">
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>Chargement...</span>
                  </>
                ) : (
                  <>
                    <span>Charger plus</span>
                    <ChevronDown className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const RecentActivitiesSkeleton: React.FC = () => {
  const ActivityItemSkeleton = () => (
    <div className="flex items-center space-x-4 py-3 border-b border-gray-100 last:border-b-0">
      <div className="w-2 h-2 rounded-full bg-gray-200 animate-pulse"></div>
      <div className="flex-1">
        <div className="h-4 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
        <div className="h-3 w-32 bg-gray-200 rounded animate-pulse"></div>
      </div>
      <div className="h-3 w-16 bg-gray-200 rounded animate-pulse"></div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="h-6 w-40 bg-gray-200 rounded animate-pulse mb-4"></div>
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <ActivityItemSkeleton key={i} />
        ))}
      </div>
    </div>
  );
};

export default RecentActivities;

